<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020-21 (original work) Open Assessment Technologies SA ;
    /**
     * @param {string} checkedLabel - label which will be shown on the left/right of switch (depends on rtl) for checked value
     * @param {string} uncheckedLabel - label which will be shown on the left/right of switch (depends on rtl) for unchecked value
     * @param {string} [ariaLabel] - aria-label for screenreader; if used, checkedLabel and uncheckedLabel won't be announced
     * @param {string} [ariaLabelledBy] - aria-labelledby for screenreader; if used, checkedLabel and uncheckedLabel won't be announced
     * @param {boolean} [value=false] - checked
     * @param {boolean} [disabled=false] - disabled
     * @param {string} [dataTestId=null] - allows to set `data-test-id` attribute, used for testing
     * @fires change - fires when value has been changed
     */

    import { createEventDispatcher } from 'svelte';

    export let checkedLabel = '';
    export let uncheckedLabel = '';
    export let ariaLabel;
    export let ariaLabelledBy;
    export let value = false;
    export let disabled = false;
    export let dataTestId = null;

    const dispatch = createEventDispatcher();

    $: label = value ? checkedLabel : uncheckedLabel;

    /**
     * Just proxy function from native input event to dispatch new value to custom event
     * @param {Event} event - native input event object
     */
    function handleInput(event) {
        dispatch('change', event.target.checked);
    }
</script>

<style>
    @add-mixin dir-style label, --dir-slider-translate, var(--switchCircleLeftOffset), var(--switchCircleRightOffset);
    @add-mixin dir-style label, --dir-checked-slider-translate, var(--switchCircleRightOffset),
        var(--switchCircleLeftOffset);

    label {
        --switchHeight: 3rem;
        --switchWidth: 6rem;
        --switchCircleSize: 2rem;

        --switchCircleLeftOffset: 0.25rem;
        --switchCircleRightOffset: calc(
            var(--switchWidth) - var(--switchCircleSize) - var(--switchCircleLeftOffset) - (var(--border-medium) * 2)
        );

        display: flex;
        align-items: center;

        position: relative;

        & input {
            @add-mixin visually-hidden;

            &:checked ~ .slider {
                background-color: var(--color-text-default);

                &:before {
                    border: none;

                    transform: translateX(var(--dir-checked-slider-translate));
                }
            }

            &:disabled ~ .slider,
            &:disabled ~ .label {
                cursor: not-allowed;
            }
        }

        & .selection {
            position: absolute;

            width: 100%;
            height: 100%;
        }

        & .slider {
            position: relative;

            width: var(--switchWidth);
            height: var(--switchHeight);

            min-width: var(--switchWidth);
            min-height: var(--switchHeight);

            border: var(--border-medium) var(--color-border-default) solid;
            border-radius: var(--radius-xlarge);

            background-color: var(--color-bg-default);

            cursor: pointer;

            transition: background-color 0.2s, transform 0.2s;

            &:before {
                position: absolute;
                content: '';
                left: 0;
                top: var(--switchCircleLeftOffset);

                width: var(--switchCircleSize);
                height: var(--switchCircleSize);

                border: var(--border-medium) var(--color-border-default) solid;
                border-radius: var(--radius-circular);

                transform: translateX(var(--dir-slider-translate));

                background-color: var(--color-bg-default);

                transition: background-color 0.2s, transform 0.2s;
            }
        }

        & .label {
            position: relative;

            padding-inline-start: var(--space-1x);

            font-size: var(--fontsize-body);
            color: var(--color-text-default);

            cursor: pointer;
        }

        & :global(input:focus-visible ~ .selection) {
            @add-mixin simple-outline;
        }
    }
</style>

<label data-test-id={dataTestId ? dataTestId : void 0}>
    <input
        type="checkbox"
        role="switch"
        {disabled}
        aria-checked={value}
        aria-label={ariaLabel ? ariaLabel : void 0}
        aria-labelledby={ariaLabelledBy ? ariaLabelledBy : void 0}
        bind:checked={value}
        on:input={handleInput} />
    <span class="selection" />
    <span class="slider" />
    <span class="label" aria-hidden={ariaLabel || ariaLabelledBy ? true : void 0}>{label}</span>
</label>
